import React, { useState, useEffect } from 'react';
import { X, User, Mail, Shield, Briefcase, Building, Clock, Save, LogOut, Camera, Bell, Moon, Sun, Monitor, Lock, KeyRound, Copy, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { employeeAPI, userAPI, departmentAPI, authAPI, adminSecurityAPI } from '../lib/api';
import { format } from 'date-fns';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const UserProfileModal = ({ isOpen, onClose, onLogout }: { isOpen: boolean; onClose: () => void; onLogout: () => void }) => {
  const { user, refreshUser } = useAuth();
  const { darkMode, themePreference, setTheme, THEME_MODES } = useTheme();
  const [activeTab, setActiveTab] = useState('profile');
  const [employee, setEmployee] = useState<Record<string, unknown> | null>(null);
  const [department, setDepartment] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: string; text: string } | null>(null);
  
  // Settings state
  const [settings, setSettings] = useState({
    emailNotifications: true,
    deadlineReminders: true,
    assignmentAlerts: true,
  });

  // #48 — Password change form state
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwSubmitting, setPwSubmitting] = useState(false);
  const [pwMessage, setPwMessage] = useState<{ type: string; text: string } | null>(null);

  // #48-ext — Rotate JWT secret (admin only)
  const [rotateOutput, setRotateOutput] = useState<{ secret: string; runbook_steps: string[]; warning: string } | null>(null);
  const [rotateLoading, setRotateLoading] = useState(false);
  const [rotateConfirmed, setRotateConfirmed] = useState(false);
  const [copyOk, setCopyOk] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Always refresh the user record from the server when the modal opens
      // so that role / department changes made by an admin are reflected
      // immediately without requiring a full page reload (Bug C). Depending
      // on `isOpen` only is intentional — we don't want to re-run on every
      // user-object identity change inside an open modal.
      refreshUser().then((freshUser) => {
        const eid = (freshUser ?? user)?.employee_id;
        if (eid) loadEmployeeData(eid);
      });
    }
  }, [isOpen]);

  const loadEmployeeData = async (employeeId: string) => {
    setLoading(true);
    let emp: Record<string, unknown> | null = null;

    // Step 1: fetch the employee record.
    try {
      const empRes = await employeeAPI.get(employeeId);
      emp = empRes.data;
      setEmployee(emp);
    } catch (error: unknown) {
      console.error('Failed to load employee data:', error);
      setEmployee(null);
    } finally {
      setLoading(false);
    }

    // Step 2: fetch the department separately so an employee-fetch failure
    // never also blanks the department row, and vice-versa (Bug A — until
    // the backend GET /departments/{id} landed, this branch silently 405'd
    // and the department field rendered as "Not assigned" for everyone).
    if (!emp?.department_id) { setDepartment(null); return; }
    try {
      const deptRes = await departmentAPI.get(emp.department_id as string);
      setDepartment(deptRes.data);
    } catch (error: unknown) {
      console.error('Failed to load department data:', error);
      setDepartment(null);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      // In a real app, you'd save these to the backend
      localStorage.setItem('userSettings', JSON.stringify(settings));
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: unknown) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  // #48 — Self-service password change handler.
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMessage(null);

    if (pwForm.next.length < 8) {
      setPwMessage({ type: 'error', text: 'New password must be at least 8 characters.' });
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      setPwMessage({ type: 'error', text: 'New password and confirmation do not match.' });
      return;
    }
    if (pwForm.next === pwForm.current) {
      setPwMessage({ type: 'error', text: 'New password must differ from your current password.' });
      return;
    }

    try {
      setPwSubmitting(true);
      await authAPI.changePassword(pwForm.current, pwForm.next, pwForm.confirm);
      setPwForm({ current: '', next: '', confirm: '' });
      setPwMessage({
        type: 'success',
        text: 'Password updated. All other sessions have been signed out.',
      });
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: unknown } } };
      const detail = e?.response?.data?.detail;
      const text = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => (d && typeof (d as { msg?: unknown }).msg === 'string' ? (d as { msg: string }).msg : JSON.stringify(d))).join(' ')
          : 'Failed to change password.';
      setPwMessage({ type: 'error', text });
    } finally {
      setPwSubmitting(false);
    }
  };

  // #48-ext — Rotate JWT secret tooling (admin only). Generates a fresh
  // secret + runbook steps. Does NOT mutate the running secret — the
  // admin pastes it into the env panel and redeploys per the runbook.
  const handleGenerateJwtSecret = async () => {
    setRotateOutput(null);
    setCopyOk(false);
    try {
      setRotateLoading(true);
      const { data } = await adminSecurityAPI.generateJwtSecret();
      setRotateOutput({
        secret: data.secret,
        runbook_steps: data.runbook_steps,
        warning: data.warning,
      });
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: unknown } } };
      const detail = e?.response?.data?.detail;
      setPwMessage({
        type: 'error',
        text: typeof detail === 'string' ? detail : 'Failed to generate secret.',
      });
    } finally {
      setRotateLoading(false);
    }
  };

  const copySecretToClipboard = async (secret: string) => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 2500);
    } catch {
      // Fallback: select-and-prompt is a CRA edge case; we surface the
      // value in a focusable input so the admin can manually copy.
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'ADMIN': return { bg: '#FEE2E2', text: '#DC2626' };
      case 'DIRECTOR': return { bg: '#F3E8FF', text: '#7C3AED' };
      case 'LEAD': return { bg: '#E8F0F7', text: '#2B5C82' };
      default: return { bg: '#DEF7E9', text: '#3E7B58' };
    }
  };

  /** Human-readable label for the raw role enum value. */
  const getRoleLabel = (role?: string | null): string => {
    switch (role) {
      case 'ADMIN':     return 'Administrator';
      case 'DIRECTOR':  return 'Director';
      case 'LEAD':      return 'Project Lead';
      case 'USER':      return 'Standard User';
      case 'READ_ONLY': return 'Read-Only';
      default:          return role ?? 'Unknown';
    }
  };

  /** Short description of what the role can do — surfaced as a hover
   *  tooltip on every role display site. Helps Kevin's new hires
   *  understand their access without digging through docs. */
  const getRoleDescription = (role?: string | null): string => {
    switch (role) {
      case 'ADMIN':
        return 'Full system access — manage users, departments, and every record across the workspace.';
      case 'DIRECTOR':
        return 'Cross-team oversight — read everything, edit projects, contracts, and team assignments.';
      case 'LEAD':
        return 'Project owner — manage tasks, assignments, and notes for projects you lead.';
      case 'USER':
        return 'Standard access — view your assignments and update progress on tasks you own.';
      case 'READ_ONLY':
        return 'View-only access — read every record in your scope; no edits.';
      default:
        return 'Role-specific access. Contact an administrator for details.';
    }
  };

  /** Wraps role-display children in a tooltip showing the description.
   *  Centralised so every display site stays in sync. */
  const RoleTooltip: React.FC<{ role?: string | null; children: React.ReactNode }> = ({ role, children }) => (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className="inline-flex" data-testid="role-tooltip-trigger">
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          className="max-w-xs text-xs leading-snug"
          data-testid="role-tooltip-content"
        >
          <p className="font-semibold mb-1">{getRoleLabel(role)}</p>
          <p>{getRoleDescription(role)}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  if (!isOpen) return null;

  const roleColors = getRoleBadgeColor(user?.role);

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden bg-[var(--bg-primary)]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b relative" style={{ borderColor: 'var(--border-primary)', background: darkMode ? 'linear-gradient(135deg, #1E3A5F 0%, #2563EB 100%)' : 'linear-gradient(135deg, #0B2E4E 0%, #2B5C82 100%)' }}>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/20 transition"
            >
              <X size={20} className="text-white" />
            </button>
            
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold relative bg-white/20">
                <span className="text-white">
                  {user?.name?.split(' ').map(n => n[0]).join('') || '?'}
                </span>
                <button className="absolute -bottom-1 -right-1 p-1.5 rounded-full bg-white shadow-lg hover:bg-gray-100 transition">
                  <Camera size={14} className="text-[#5A6B7A]" />
                </button>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{user?.name}</h2>
                <p className="text-white/80 text-sm">{user?.email}</p>
                <RoleTooltip role={user?.role}>
                  <span 
                    className="badge-status inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold cursor-help"
                    style={{ '--_bg': roleColors.bg, '--_text': roleColors.text } as React.CSSProperties}
                    data-testid="profile-header-role-badge"
                  >
                    {getRoleLabel(user?.role)}
                  </span>
                </RoleTooltip>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-wmt-primary" >            {[
              { id: 'profile', label: 'Profile', icon: User },
              { id: 'security', label: 'Security', icon: Lock },
              { id: 'settings', label: 'Settings', icon: Bell },
              { id: 'account', label: 'Account', icon: Shield }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition flex items-center justify-center gap-2 ${
                    activeTab === tab.id ? 'border-b-2' : ''
                  }`}
                  style={{ 
                    borderColor: activeTab === tab.id ? 'var(--alliance-steel)' : 'transparent',
                    color: activeTab === tab.id ? 'var(--alliance-steel)' : 'var(--text-muted)'
                  }}
                >
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[50vh]">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                  </div>
                ) : (
                  <>
                    {/* User Info */}
                    <div>
                      <h3 className="text-sm font-semibold uppercase mb-3 text-wmt-muted" >Account Information</h3>                      <div className="space-y-3">
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-wmt-tertiary" >                          <User className="text-alliance-steel" size={18}  />                          <div>
                            <p className="text-xs text-wmt-muted" >Full Name</p>                            <p className="text-sm font-medium text-wmt-primary" >{user?.name}</p>                          </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-wmt-tertiary" >                          <Mail className="text-alliance-steel" size={18}  />                          <div>
                            <p className="text-xs text-wmt-muted" >Email Address</p>                            <p className="text-sm font-medium text-wmt-primary" >{user?.email}</p>                          </div>
                        </div>
                        <RoleTooltip role={user?.role}>
                          <div className="flex items-center gap-3 p-3 rounded-lg bg-wmt-tertiary cursor-help" >                          <Shield className="text-alliance-steel" size={18}  />                          <div>
                            <p className="text-xs text-wmt-muted" >Role</p>                            <p className="text-sm font-medium text-wmt-primary" data-testid="profile-role-row">{getRoleLabel(user?.role)}</p>                          </div>
                        </div>
                        </RoleTooltip>
                      </div>
                    </div>

                    {/* Employee Info */}
                    {employee && (
                      <div>
                        <h3 className="text-sm font-semibold uppercase mb-3 text-wmt-muted" >Work Information</h3>                        <div className="space-y-3">
                          <div className="flex items-center gap-3 p-3 rounded-lg bg-wmt-tertiary" >                            <Briefcase className="text-status-success" size={18}  />                            <div>
                              <p className="text-xs text-wmt-muted" >Job Title</p>                              <p className="text-sm font-medium text-wmt-primary" >{employee.title || 'Not set'}</p>                            </div>
                          </div>
                          <div className="flex items-center gap-3 p-3 rounded-lg bg-wmt-tertiary" >                            <Building className="text-status-success" size={18}  />                            <div>
                              <p className="text-xs text-wmt-muted" >Department</p>                              <p className="text-sm font-medium text-wmt-primary" >{department?.name || 'Not assigned'}</p>                            </div>
                          </div>
                          <div className="flex items-center gap-3 p-3 rounded-lg bg-wmt-tertiary" >                            <Clock className="text-status-success" size={18}  />                            <div>
                              <p className="text-xs text-wmt-muted" >Standard Hours</p>                              <p className="text-sm font-medium text-wmt-primary" >{employee.standard_hours || 40}h / week</p>                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Security Tab — #48 password change + #48-ext rotate JWT */}
            {activeTab === 'security' && (
              <div className="space-y-6" data-testid="security-tab-panel">
                <div>
                  <h3 className="text-sm font-semibold uppercase mb-3 text-wmt-muted">Change Password</h3>
                  <form onSubmit={handleChangePassword} className="space-y-3" data-testid="change-password-form">
                    <div>
                      <label className="text-xs text-wmt-muted">Current password</label>
                      <input
                        type="password"
                        value={pwForm.current}
                        onChange={(e) => setPwForm((f) => ({ ...f, current: e.target.value }))}
                        className="w-full mt-1 px-3 py-2 rounded-lg border border-wmt-primary bg-wmt-tertiary text-sm text-wmt-primary"
                        autoComplete="current-password"
                        required
                        data-testid="change-password-current"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-wmt-muted">New password</label>
                      <input
                        type="password"
                        value={pwForm.next}
                        onChange={(e) => setPwForm((f) => ({ ...f, next: e.target.value }))}
                        className="w-full mt-1 px-3 py-2 rounded-lg border border-wmt-primary bg-wmt-tertiary text-sm text-wmt-primary"
                        autoComplete="new-password"
                        minLength={8}
                        required
                        data-testid="change-password-new"
                      />
                      <p className="text-xs text-wmt-muted mt-1">
                        At least 8 characters with one uppercase, one lowercase, one digit, and one special character.
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-wmt-muted">Confirm new password</label>
                      <input
                        type="password"
                        value={pwForm.confirm}
                        onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))}
                        className="w-full mt-1 px-3 py-2 rounded-lg border border-wmt-primary bg-wmt-tertiary text-sm text-wmt-primary"
                        autoComplete="new-password"
                        minLength={8}
                        required
                        data-testid="change-password-confirm"
                      />
                    </div>

                    {pwMessage && (
                      <div
                        className="p-3 rounded-lg text-sm flex items-start gap-2"
                        style={{
                          background: pwMessage.type === 'success' ? 'var(--success-bg)' : 'var(--error-bg)',
                          color: pwMessage.type === 'success' ? 'var(--success-text)' : 'var(--error-text)',
                        }}
                        data-testid="change-password-message"
                      >
                        {pwMessage.type === 'success' ? <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" /> : <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />}
                        <span>{pwMessage.text}</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={pwSubmitting || !pwForm.current || !pwForm.next || !pwForm.confirm}
                      className="alliance-btn-primary w-full py-2.5 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="change-password-submit"
                    >
                      {pwSubmitting ? (
                        <>
                          <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                          Updating…
                        </>
                      ) : (
                        <>
                          <Lock size={16} />
                          Update Password
                        </>
                      )}
                    </button>
                  </form>
                </div>

                {user?.role === 'ADMIN' && (
                  <div className="pt-4 border-t border-wmt-primary">
                    <h3 className="text-sm font-semibold uppercase mb-2 text-status-error flex items-center gap-2">
                      <AlertTriangle size={14} />
                      Rotate JWT Secret
                    </h3>
                    <p className="text-xs text-wmt-muted mb-3">
                      Generates a fresh JWT_SECRET_KEY value for you to paste into the Emergent env panel.
                      The running secret is <strong>not</strong> mutated by this button — that's a redeploy
                      step. After redeploy, every session including yours is invalidated and users see one
                      automatic re-login. Schedule for off-hours unless this is incident response.
                    </p>

                    {!rotateOutput && (
                      <>
                        {!rotateConfirmed ? (
                          <button
                            type="button"
                            onClick={() => setRotateConfirmed(true)}
                            className="w-full py-2.5 rounded-lg text-sm font-medium transition flex items-center justify-center gap-2 border border-status-error text-status-error hover:bg-[var(--error-bg)]"
                            data-testid="rotate-jwt-show-confirm"
                          >
                            <KeyRound size={16} />
                            I understand — show me the controls
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={handleGenerateJwtSecret}
                            disabled={rotateLoading}
                            className="w-full py-2.5 rounded-lg text-sm font-medium transition flex items-center justify-center gap-2 bg-[var(--error-text)] text-white disabled:opacity-50"
                            data-testid="rotate-jwt-generate"
                          >
                            {rotateLoading ? (
                              <>
                                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                Generating…
                              </>
                            ) : (
                              <>
                                <KeyRound size={16} />
                                Generate new JWT_SECRET_KEY
                              </>
                            )}
                          </button>
                        )}
                      </>
                    )}

                    {rotateOutput && (
                      <div className="space-y-3" data-testid="rotate-jwt-output">
                        <div className="p-3 rounded-lg bg-wmt-tertiary">
                          <label className="text-xs text-wmt-muted">New JWT_SECRET_KEY (96 chars)</label>
                          <div className="flex gap-2 mt-1">
                            <input
                              type="text"
                              readOnly
                              value={rotateOutput.secret}
                              onFocus={(e) => e.currentTarget.select()}
                              className="flex-1 px-2 py-1.5 rounded border border-wmt-primary bg-wmt-secondary text-xs font-mono text-wmt-primary"
                              data-testid="rotate-jwt-secret-output"
                            />
                            <button
                              type="button"
                              onClick={() => copySecretToClipboard(rotateOutput.secret)}
                              className="px-3 py-1.5 rounded border border-wmt-primary text-xs flex items-center gap-1 text-wmt-primary"
                              data-testid="rotate-jwt-copy-button"
                            >
                              <Copy size={12} />
                              {copyOk ? 'Copied' : 'Copy'}
                            </button>
                          </div>
                        </div>

                        <div
                          className="p-3 rounded-lg text-sm"
                          style={{ background: 'var(--error-bg)', color: 'var(--error-text)' }}
                        >
                          <div className="flex items-start gap-2">
                            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                            <span>{rotateOutput.warning}</span>
                          </div>
                        </div>

                        <ol className="list-decimal pl-5 space-y-1 text-xs text-wmt-primary">
                          {rotateOutput.runbook_steps.map((step, idx) => (
                            <li key={idx}>{step.replace(/^\d+\.\s*/, '')}</li>
                          ))}
                        </ol>

                        <button
                          type="button"
                          onClick={() => {
                            setRotateOutput(null);
                            setRotateConfirmed(false);
                          }}
                          className="w-full py-2 rounded-lg text-xs font-medium border border-wmt-primary text-wmt-primary"
                          data-testid="rotate-jwt-reset"
                        >
                          Done — clear from screen
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="space-y-6">
                {message && (
                  <div 
                    className="p-3 rounded-lg text-sm"
                    style={{ 
                      background: message.type === 'success' ? 'var(--success-bg)' : 'var(--error-bg)',
                      color: message.type === 'success' ? 'var(--success-text)' : 'var(--error-text)'
                    }}
                  >
                    {message.text}
                  </div>
                )}
                
                <div>
                  <h3 className="text-sm font-semibold uppercase mb-3 text-wmt-muted" >Notifications</h3>                  <div className="space-y-3">
                    <label className="flex items-center justify-between p-3 rounded-lg cursor-pointer transition bg-wmt-tertiary" >                      <div className="flex items-center gap-3">
                        <Mail className="text-alliance-steel" size={18}  />                        <div>
                          <p className="text-sm font-medium text-wmt-primary" >Email Notifications</p>                          <p className="text-xs text-wmt-muted" >Receive updates via email</p>                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings.emailNotifications}
                        onChange={(e) => setSettings(s => ({ ...s, emailNotifications: e.target.checked }))}
                        className="w-5 h-5 rounded"
                      />
                    </label>
                    
                    <label className="flex items-center justify-between p-3 rounded-lg cursor-pointer transition bg-wmt-tertiary" >                      <div className="flex items-center gap-3">
                        <Clock className="text-status-warning" size={18}  />                        <div>
                          <p className="text-sm font-medium text-wmt-primary" >Deadline Reminders</p>                          <p className="text-xs text-wmt-muted" >Get notified before deadlines</p>                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings.deadlineReminders}
                        onChange={(e) => setSettings(s => ({ ...s, deadlineReminders: e.target.checked }))}
                        className="w-5 h-5 rounded"
                      />
                    </label>
                    
                    <label className="flex items-center justify-between p-3 rounded-lg cursor-pointer transition bg-wmt-tertiary" >                      <div className="flex items-center gap-3">
                        <Briefcase className="text-status-success" size={18}  />                        <div>
                          <p className="text-sm font-medium text-wmt-primary" >Assignment Alerts</p>                          <p className="text-xs text-wmt-muted" >Notify when assigned to tasks</p>                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings.assignmentAlerts}
                        onChange={(e) => setSettings(s => ({ ...s, assignmentAlerts: e.target.checked }))}
                        className="w-5 h-5 rounded"
                      />
                    </label>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold uppercase mb-3 text-wmt-muted" >Appearance</h3>                  <div className="space-y-2">
                    {/* Light Mode */}
                    <label 
                      className="flex items-center justify-between p-3 rounded-lg cursor-pointer transition"
                      style={{ 
                        background: themePreference === THEME_MODES.LIGHT ? 'var(--bg-accent)' : 'var(--bg-tertiary)',
                        border: themePreference === THEME_MODES.LIGHT ? '2px solid var(--alliance-steel)' : '2px solid transparent'
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <Sun size={18} className="text-amber-500" />
                        <div>
                          <p className="text-sm font-medium text-wmt-primary" >Light</p>                          <p className="text-xs text-wmt-muted" >Always use light theme</p>                        </div>
                      </div>
                      <input
                        type="radio"
                        name="theme"
                        checked={themePreference === THEME_MODES.LIGHT}
                        onChange={() => setTheme(THEME_MODES.LIGHT)}
                        className="w-4 h-4"
                      />
                    </label>
                    
                    {/* Dark Mode */}
                    <label 
                      className="flex items-center justify-between p-3 rounded-lg cursor-pointer transition"
                      style={{ 
                        background: themePreference === THEME_MODES.DARK ? 'var(--bg-accent)' : 'var(--bg-tertiary)',
                        border: themePreference === THEME_MODES.DARK ? '2px solid var(--alliance-steel)' : '2px solid transparent'
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <Moon className="text-wmt-secondary" size={18}  />                        <div>
                          <p className="text-sm font-medium text-wmt-primary" >Dark</p>                          <p className="text-xs text-wmt-muted" >Always use dark theme</p>                        </div>
                      </div>
                      <input
                        type="radio"
                        name="theme"
                        checked={themePreference === THEME_MODES.DARK}
                        onChange={() => setTheme(THEME_MODES.DARK)}
                        className="w-4 h-4"
                      />
                    </label>
                    
                    {/* System Mode */}
                    <label 
                      className="flex items-center justify-between p-3 rounded-lg cursor-pointer transition"
                      style={{ 
                        background: themePreference === THEME_MODES.SYSTEM ? 'var(--bg-accent)' : 'var(--bg-tertiary)',
                        border: themePreference === THEME_MODES.SYSTEM ? '2px solid var(--alliance-steel)' : '2px solid transparent'
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <Monitor className="text-alliance-steel" size={18}  />                        <div>
                          <p className="text-sm font-medium text-wmt-primary" >System</p>                          <p className="text-xs text-wmt-muted" >Match your OS preference</p>                        </div>
                      </div>
                      <input
                        type="radio"
                        name="theme"
                        checked={themePreference === THEME_MODES.SYSTEM}
                        onChange={() => setTheme(THEME_MODES.SYSTEM)}
                        className="w-4 h-4"
                      />
                    </label>
                  </div>
                  
                  {themePreference === THEME_MODES.SYSTEM && (
                    <p className="text-xs mt-2 px-3 text-wmt-muted" >                      Currently using {darkMode ? 'dark' : 'light'} theme based on your system settings
                    </p>
                  )}
                </div>

                <button
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="alliance-btn-primary w-full py-2.5 text-sm font-medium flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Save Settings
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Account Tab */}
            {activeTab === 'account' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-semibold uppercase mb-3 text-wmt-muted" >Session</h3>                  <div className="p-4 rounded-lg bg-wmt-tertiary" >                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-wmt-muted" >Logged in as</span>                      <span className="text-sm font-medium text-wmt-primary" >{user?.email}</span>                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-wmt-muted" >Account type</span>                      <RoleTooltip role={user?.role}>
                        <span 
                          className="badge-status text-xs font-semibold px-2 py-1 rounded cursor-help"
                          style={{ '--_bg': roleColors.bg, '--_text': roleColors.text } as React.CSSProperties}
                          data-testid="account-type-badge"
                        >
                          {getRoleLabel(user?.role)}
                        </span>
                      </RoleTooltip>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold uppercase mb-3 text-status-error" >Danger Zone</h3>                  <button
                    onClick={onLogout}
                    className="w-full py-2.5 rounded-lg text-sm font-medium transition flex items-center justify-center gap-2 bg-[var(--error-text)] text-white"
                  >
                    <LogOut size={16} />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default UserProfileModal;
